/**
 * #142 · Pedidos de rollos — el lado del CLIENTE.
 *
 * Es la primera pestaña de Etiquetas para una marca, y reemplaza a "Órdenes",
 * que no era suya: `label_order` es la compra de XYVONA al impresor
 * (`supplier` es lo que figura en el packing list del proveedor), y quien le
 * compra al impresor es XYVONA bajo su propio tenant. Después
 * `backoffice_roll_assign()` mueve el rollo al cliente.
 *
 * La marca no puede estar del otro lado de esa relación aunque quisiera: la
 * generación firma con la clave HMAC de la plataforma —una sola, por versión,
 * no por cliente (ADR-004)—, así que un cliente generando códigos estaría
 * acuñando etiquetas válidas para todo el sistema. Pedir es la forma que tiene
 * de participar sin sostener esa llave.
 *
 * La contraparte de esta pantalla es `hq/RequestsPage`: lo que se crea acá cae
 * en la bandeja de XYVONA.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import { rollRequestKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import type { MessageKey } from '@/i18n/messages';
import { useI18n } from '@/i18n/useI18n';
import { Badge, type Tone } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { QueryBoundary } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { Dialog } from '../products/ui/Dialog';
import { LabelsTabs } from './LabelsTabs';
import { RollRequestForm, type RollRequestDraft } from './RollRequestForm';

type Pedido = Schemas['RollRequestOut'];

/** Mismos tonos que la bandeja del back-office: es el mismo objeto. */
const TONO: Record<string, Tone> = {
  pending: 'progress',
  approved: 'neutral',
  fulfilled: 'neutral',
  rejected: 'critical',
};

const ETIQUETA: Record<string, MessageKey> = {
  pending: 'requests.state.pending',
  approved: 'requests.state.approved',
  fulfilled: 'requests.state.fulfilled',
  rejected: 'requests.state.rejected',
};

export function RollRequestsPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();
  const [abierto, setAbierto] = useState(false);

  const pedidos = useApiQuery<Pedido[]>(rollRequestKeys.list(), '/api/v1/roll-requests');

  const crear = useMutation<Pedido, unknown, RollRequestDraft>({
    mutationFn: (draft) => api.post<Pedido>('/api/v1/roll-requests', draft),
    onSuccess: () => {
      setAbierto(false);
      void queryClient.invalidateQueries({ queryKey: rollRequestKeys.all });
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <LabelsTabs active="requests" />

      <Card>
        <CardHeader
          title={t('rollRequests.title')}
          description={t('rollRequests.description')}
          actions={
            // Pedir es escribir sobre la custodia: `POST /roll-requests` exige
            // `admin`. Un `analyst` ve sus pedidos y no el botón.
            can('admin') ? (
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  crear.reset();
                  setAbierto(true);
                }}
              >
                {t('rollRequests.new')}
              </Button>
            ) : undefined
          }
        />

        <CardBody className="p-0">
          <QueryBoundary
            query={pedidos}
            empty={{
              title: t('rollRequests.empty'),
              detail: t('rollRequests.empty.detail'),
            }}
          >
            {(filas) => (
              <Table caption={t('rollRequests.title')}>
                <thead>
                  <tr>
                    <Th className="text-right">{t('rollRequests.col.quantity')}</Th>
                    <Th>{t('rollRequests.col.neededBy')}</Th>
                    <Th>{t('rollRequests.col.notes')}</Th>
                    <Th>{t('rollRequests.col.state')}</Th>
                    <Th>{t('rollRequests.col.resolution')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((fila) => (
                    <tr key={fila.request_id}>
                      <Td className="text-right tabular-nums font-medium text-ink">
                        {fmt.number(fila.quantity)}
                        <span className="block text-xs font-normal text-ink-muted">
                          {fmt.relative(fila.created_at)}
                        </span>
                      </Td>
                      <Td className="text-xs">
                        {fila.needed_by === null ? '—' : fmt.date(fila.needed_by)}
                      </Td>
                      <Td className="max-w-xs truncate text-xs text-ink-muted">
                        {fila.notes ?? '—'}
                      </Td>
                      <Td>
                        <Badge tone={TONO[fila.status] ?? 'neutral'}>
                          {t(ETIQUETA[fila.status] ?? 'requests.state.pending')}
                        </Badge>
                      </Td>
                      {/*
                        El motivo de un rechazo es lo único que explica por qué
                        un pedido no avanzó. Sin esta columna, el cliente ve
                        "rechazado" y tiene que llamar por teléfono.
                      */}
                      <Td className="max-w-xs truncate text-xs text-ink-muted">
                        {fila.resolution ?? '—'}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </QueryBoundary>
        </CardBody>
      </Card>

      <Dialog
        open={abierto}
        title={t('rollRequests.new')}
        description={t('rollRequests.new.description')}
        onClose={() => {
          setAbierto(false);
        }}
      >
        <RollRequestForm
          onSubmit={(draft) => {
            crear.mutate(draft);
          }}
          onCancel={() => {
            setAbierto(false);
          }}
          isSubmitting={crear.isPending}
          error={crear.error}
        />
      </Dialog>
    </div>
  );
}
